import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { recordPageVisit, createMemory, getMemories, likeMemory, aiEnhanceMemory } from "@/lib/api";
import { 
  Heart, 
  Plus, 
  Sparkles, 
  BookHeart, 
  MessageCircle,
  Camera,
  Star,
  Users,
  Loader2,
  Send,
  ThumbsUp,
  Calendar,
  Globe,
  Lock,
  Wand2
} from "lucide-react";

const MEMORY_TYPES = {
  story: { label: "memories.types.story", icon: BookHeart, description: "memories.types.storyDesc" },
  milestone: { label: "memories.types.milestone", icon: Star, description: "memories.types.milestoneDesc" },
  gratitude: { label: "memories.types.gratitude", icon: Heart, description: "memories.types.gratitudeDesc" },
  photo_caption: { label: "memories.types.photo", icon: Camera, description: "memories.types.photoDesc" }
};

export default function JoyfulMemories() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [memories, setMemories] = useState({ my_memories: [], community_memories: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("my");
  const [isEnhancing, setIsEnhancing] = useState(false);
  
  const [newMemory, setNewMemory] = useState({
    patient_name: "",
    title: "",
    content: "",
    memory_type: "story",
    date_of_memory: "",
    is_public: false
  });

  useEffect(() => {
    recordPageVisit("memories").catch(console.error);
    loadMemories();
  }, []);

  const loadMemories = async () => {
    try {
      setIsLoading(true);
      const data = await getMemories(true);
      setMemories(data);
    } catch (error) {
      console.error("Failed to load memories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateMemory = async () => {
    if (!newMemory.patient_name || !newMemory.title || !newMemory.content) {
      toast.error(t("memories.fillRequired"));
      return;
    }
    
    setIsSubmitting(true);
    try {
      await createMemory(newMemory);
      toast.success(t("memories.saveSuccess"));
      setShowNewDialog(false);
      setNewMemory({
        patient_name: "",
        title: "",
        content: "",
        memory_type: "story",
        date_of_memory: "",
        is_public: false
      });
      loadMemories();
    } catch (error) {
      toast.error(t("memories.saveFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnhanceContent = async () => {
    if (!newMemory.content) {
      toast.error(t("memories.writeFirst"));
      return;
    }
    
    setIsEnhancing(true);
    try {
      const result = await aiEnhanceMemory(newMemory.content);
      setNewMemory(prev => ({ ...prev, content: result.enhanced_content }));
      toast.success(t("memories.enhanceSuccess"));
    } catch (error) {
      toast.error(t("memories.enhanceFailed"));
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleLike = async (memoryId) => {
    try {
      await likeMemory(memoryId);
      loadMemories();
    } catch (error) {
      console.error("Failed to like memory:", error);
    }
  };

  // Render a memory card - uses t from translation context
  const renderMemoryCard = (memory, showLike = false) => {
    const TypeIcon = MEMORY_TYPES[memory.memory_type]?.icon || BookHeart;
    
    return (
      <Card key={memory.id} className="glass-card border-0 hover:shadow-lg transition-all">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <TypeIcon className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-serif font-medium">{memory.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {t("memories.forPatient", { name: memory.patient_name })}
                </p>
              </div>
            </div>
            <Badge variant="outline" className="text-xs">
              {t(MEMORY_TYPES[memory.memory_type]?.label) || t("memories.defaultLabel")}
            </Badge>
          </div>
          
          <p className="text-muted-foreground leading-relaxed mb-4 whitespace-pre-line">
            {memory.content}
          </p>
          
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {memory.is_public ? (
                <Globe className="w-3 h-3" />
              ) : (
                <Lock className="w-3 h-3" />
              )}
              <span>{memory.is_public ? t("memories.shared") : t("memories.private")}</span>
              {memory.date_of_memory && (
                <>
                  <span>•</span>
                  <Calendar className="w-3 h-3" />
                  <span>{memory.date_of_memory}</span>
                </>
              )}
            </div>
            
            {showLike && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleLike(memory.id)}
                className="text-muted-foreground hover:text-primary"
              >
                <ThumbsUp className="w-4 h-4 mr-1" />
                {memory.likes || 0}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-pink-50/50 to-background">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
            <h2 className="text-xl font-serif font-semibold mb-2">{t("memories.shareTitle")}</h2>
            <p className="text-muted-foreground mb-4">
              {t("memories.signInPrompt")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/30 to-background" data-testid="memories-page">
      {/* Hero */}
      <section className="section-padding bg-gradient-to-br from-pink-50 to-background">
        <div className="container-max">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <Badge variant="secondary" className="mb-4">
              <Heart className="w-3 h-3 mr-1 text-pink-500" />
              {t("memories.celebrateLife")}
            </Badge>
            <h1 className="heading-1 text-foreground">
              {t("memories.title")} <span className="text-pink-600">{t("memories.titleHighlight")}</span>
            </h1>
            <p className="body-lg text-muted-foreground">
              {t("memories.description")}
            </p>
            
            <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
              <DialogTrigger asChild>
                <Button className="rounded-full px-8 py-6 mt-4 bg-pink-600 hover:bg-pink-700">
                  <Plus className="w-5 h-5 mr-2" />
                  {t("memories.createNew")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif text-xl flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-pink-500" />
                    {t("memories.dialogTitle")}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>{t("memories.memoryType")}</Label>
                    <Select
                      value={newMemory.memory_type}
                      onValueChange={(value) => setNewMemory({ ...newMemory, memory_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(MEMORY_TYPES).map(([key, { label, description }]) => (
                          <SelectItem key={key} value={key}>
                            <div>
                              <div className="font-medium">{t(label)}</div>
                              <div className="text-xs text-muted-foreground">{t(description)}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="patient_name">{t("memories.forPatientLabel")} *</Label>
                    <Input
                      id="patient_name"
                      placeholder={t("memories.patientPlaceholder")}
                      value={newMemory.patient_name}
                      onChange={(e) => setNewMemory({ ...newMemory, patient_name: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="title">{t("memories.titleLabel")} *</Label>
                    <Input
                      id="title"
                      placeholder={t("memories.titlePlaceholder")}
                      value={newMemory.title}
                      onChange={(e) => setNewMemory({ ...newMemory, title: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="content">{t("memories.contentLabel")} *</Label>
                    <Textarea
                      id="content"
                      placeholder={t("memories.contentPlaceholder")}
                      rows={6}
                      value={newMemory.content}
                      onChange={(e) => setNewMemory({ ...newMemory, content: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleEnhanceContent}
                      disabled={isEnhancing || !newMemory.content}
                      className="mt-2"
                    >
                      {isEnhancing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Wand2 className="w-4 h-4 mr-2" />
                      )}
                      {t("memories.enhanceAI")}
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="date">{t("memories.dateLabel")}</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newMemory.date_of_memory}
                      onChange={(e) => setNewMemory({ ...newMemory, date_of_memory: e.target.value })}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <Label>{t("memories.shareWithCommunity")}</Label>
                      <p className="text-xs text-muted-foreground">
                        {t("memories.shareDescription")}
                      </p>
                    </div>
                    <Switch
                      checked={newMemory.is_public}
                      onCheckedChange={(checked) => setNewMemory({ ...newMemory, is_public: checked })}
                    />
                  </div>
                  
                  <Button 
                    onClick={handleCreateMemory} 
                    className="w-full rounded-full bg-pink-600 hover:bg-pink-700"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Heart className="w-4 h-4 mr-2" />
                    )}
                    {t("memories.saveMemory")}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </section>

      {/* Memories */}
      <section className="section-padding">
        <div className="container-max">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
            <TabsList className="bg-white shadow-soft">
              <TabsTrigger value="my" className="rounded-full">
                <Lock className="w-4 h-4 mr-2" />
                {t("memories.myMemories")} ({memories.my_memories?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="community" className="rounded-full">
                <Users className="w-4 h-4 mr-2" />
                {t("memories.community")} ({memories.community_memories?.length || 0})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="my" className="mt-6">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : memories.my_memories?.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t("memories.noMemoriesYet")}</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {memories.my_memories?.map((memory) => renderMemoryCard(memory, false))}
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="community" className="mt-6">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : memories.community_memories?.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">{t("memories.noCommunityMemories")}</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-6">
                  {memories.community_memories?.map((memory) => renderMemoryCard(memory, true))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-pink-600 text-white">
        <div className="container-max text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <h2 className="heading-2 text-white">{t("memories.ctaTitle")}</h2>
            <p className="body-lg text-white/80">
              {t("memories.ctaDescription")}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
